/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Validators } from 'Modules'
import { RequestParams, ResponseCatalog } from 'Models'
import {
	divideInputData,
	getArrayInitialValue,
	isEmptyValue,
	hiddinEntetiesSelect,
	intersection,
} from 'Helpers'
import { useEffectOnce } from 'Src/hooks/useEffectOnce'
import { DEFAULT_FIELD_KEY, SelectComponent, SelectComponentProps } from 'LibNeo'
import { SelectItem } from 'Types/item'
import { debounce } from 'lodash'
import { Callback } from 'Src/types/utilTypes'
import { RequestOptions } from 'Types/requestOptions'

const { setData, clear } = hiddinEntetiesSelect

export interface NeoSelectConnectedProps<T extends SelectItem> extends SelectComponentProps<T> {
	initialValue?: string | string[] | number | number[]
	required?: boolean
	data?: ResponseCatalog<T>
	params?: RequestParams
	paramsData?: Record<string, unknown>
	getItems?: (params?: RequestParams, options?: RequestOptions, callback?: Callback) => void
	getMissingItem?: (id: number | string) => void
	setRequestParams?: (requestParams: RequestParams) => void
	onChange?: (value) => void
	clearItems?: () => void
	isNeedCut?: boolean
	hasSearch?: boolean
}

export function NeoSelectConnected<T extends SelectItem>({
	data: items,
	fieldName,
	fieldKey = DEFAULT_FIELD_KEY,
	form,
	disabled = false,
	className,
	getItems,
	params,
	dataQa,
	initialValue,
	setRequestParams,
	fieldOptions,
	paramsData,
	required = false,
	showArrow = true,
	onChange,
	mode,
	isNeedCut = false,
	hasSearch = true,
	...props
}: NeoSelectConnectedProps<T>) {
	const [noMore, setNoMore] = useState(false)
	const [isLimit, setIsLimit] = useState(false)
	const requestCount = useRef(0)
	const changed = useRef(false)
	const searchValue = useRef<string | null>(null)

	const autoFillValue = () => {
		form?.setFieldsValue({ [fieldName]: items.entries[0][fieldKey] })
		onChange?.(items.entries[0][fieldKey])
	}

	useEffectOnce(() => {
		setRequestParams(Object.assign(new RequestParams(), params, paramsData))
		return () => {
			props?.clearItems?.()
			form?.resetFields([fieldName])
		}
	})

	useEffect(() => {
		const initials = getArrayInitialValue(fieldOptions?.initialValue || initialValue)
		const { entries, current_page, per_page } = items
		if (required && entries?.length === 1 && !params?.query) autoFillValue()
		if (!initials?.length) return

		let missingFieldKeys = []
		if (items?.entries.length) {
			missingFieldKeys = initials.filter(
				(key: T[typeof fieldKey]) => !items.entries.find((item) => item?.[fieldKey] === key)
			)
		}
		if (!noMore && missingFieldKeys?.length && !isLimit) {
			getItems(new RequestParams(1, 30, null, { [fieldKey]: missingFieldKeys.toString() }), {
				searchMissing: true,
			})
			requestCount.current += 1
		}
		if (
			(current_page && per_page && entries.length < (current_page - 1) * per_page) ||
			(entries.length && (!current_page || !per_page))
		) {
			setNoMore(true)
		}
		if (requestCount.current === 3) {
			setIsLimit(true)
			if (!changed.current && isNeedCut) {
				const val = form?.getFieldValue(fieldName) ?? []
				const currentIds = Array.isArray(val) ? val : [val]
				const { hidden, show } = divideInputData(currentIds, missingFieldKeys)
				setData(fieldName, hidden)
				form?.setFieldsValue({ [fieldName]: show })
				changed.current = true
			}
		}
	}, [items?.entries.length])

	useEffect(
		() => () => {
			clear()
		},
		[]
	)

	useEffect(() => {
		if (paramsData) {
			const { per_page } = items
			const newRequestParams = new RequestParams(1, per_page, null, paramsData)
			setRequestParams(newRequestParams)
			getItems(newRequestParams)
		}
	}, [JSON.stringify(paramsData)])

	const onScroll = useCallback(
		(e: React.UIEvent<HTMLElement>) => {
			const { query } = params
			const { per_page, current_page, total_entries } = items
			const target = e.target as HTMLElement
			const flagEnd = Math.ceil(target.scrollTop) + target.offsetHeight >= target.scrollHeight
			if (
				current_page &&
				per_page &&
				!noMore &&
				flagEnd &&
				(!total_entries || current_page * per_page < total_entries)
			) {
				const newRequestParams = new RequestParams(current_page + 1, per_page, query, paramsData)
				setRequestParams(newRequestParams)
				getItems(newRequestParams, { loadMore: true })
			}
		},
		[items, params, JSON.stringify(paramsData)]
	)
	const callback = (value: number[], addValue = false) => {
		if (!addValue) return
		const item = intersection(form?.getFieldValue(fieldName) ?? [], value)
		if (item.length) return
		form?.setFieldsValue({ [fieldName]: [...(form?.getFieldValue(fieldName) ?? []), ...value] })
	}
	const realSearch = useCallback(
		(value: string) => {
			if (value || searchValue.current) {
				setNoMore(false)
				const newRequestParams = new RequestParams(1, 30, value, paramsData)
				setRequestParams(newRequestParams)
				getItems(newRequestParams, { isNullSearch: value === null }, callback)
				searchValue.current = value
			}
		},
		[JSON.stringify(paramsData)]
	)

	const onSearch = debounce(realSearch, 300)
	const onChangeSelect = useCallback(
		(value: T[typeof fieldKey]) => {
			form?.setFieldsValue({ [fieldName]: value })
			onChange?.(value)
			onSearch(null)
		},
		[onChange, JSON.stringify(paramsData)]
	)

	const getInitialValue = () => {
		if (mode === 'multiple') return getArrayInitialValue(initialValue)
		if (isEmptyValue(initialValue)) return undefined
		return initialValue
	}

	return (
		<SelectComponent
			{...props}
			dataQa={dataQa}
			items={items?.entries}
			fieldKey={fieldKey}
			fieldName={fieldName}
			form={form}
			disabled={disabled}
			className={className}
			onPopupScroll={onScroll}
			fieldOptions={{
				initialValue: getInitialValue(),
				rules: [
					{
						validator: Validators.checkRequired({ required }),
					},
				],
				...fieldOptions,
			}}
			onChange={onChangeSelect}
			onSearch={hasSearch ? onSearch : null}
			filterOption={false}
			showArrow={showArrow}
			mode={mode}
		/>
	)
}
